import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { formatCurrency } from '../../utils/calculations';
import { useNavigate } from 'react-router-dom';
interface PaymentConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void; // Kept for backward compatibility but we'll use navigation
  amount: number;
  date: string;
  property: string;
}
export function PaymentConfirmation({
  isOpen,
  onClose,
  amount,
  date,
  property
}: PaymentConfirmationProps) {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const handleProceed = () => {
    setIsProcessing(true);
    // Small delay for UX
    setTimeout(() => {
      setIsProcessing(false);
      onClose();
      navigate('/tenant/payment-options');
    }, 500);
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-1586">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1587">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-1588" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden z-10" data-id="element-1589">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-6 text-white text-center relative" data-id="element-1590">
            <button onClick={onClose} className="absolute top-4 right-4 text-blue-100 hover:text-white transition-colors" data-id="element-1591">
              <X className="h-6 w-6" data-id="element-1592" />
            </button>
            <div className="mx-auto w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-3 border-2 border-blue-400" data-id="element-1593">
              <ShieldCheck className="h-6 w-6 text-white" data-id="element-1594" />
            </div>
            <h3 className="text-xl font-bold" data-id="element-1595">Review Payment</h3>
            <p className="text-blue-100 text-sm mt-1" data-id="element-1596">Secure Transaction</p>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6" data-id="element-1597">
            <div className="text-center" data-id="element-1598">
              <p className="text-gray-500 text-sm mb-1" data-id="element-1599">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900" data-id="element-1600">
                {formatCurrency(amount)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100" data-id="element-1601">
              <div className="flex justify-between text-sm" data-id="element-1602">
                <span className="text-gray-500" data-id="element-1603">Property</span>
                <span className="font-medium text-gray-900 text-right" data-id="element-1604">
                  {property}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1605">
                <span className="text-gray-500" data-id="element-1606">Date</span>
                <span className="font-medium text-gray-900" data-id="element-1607">{date}</span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1608">
                <span className="text-gray-500" data-id="element-1609">Service Fee</span>
                <span className="font-medium text-gray-900" data-id="element-1610">₦0.00</span>
              </div>
            </div>

            <div className="flex items-start p-3 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1611">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-1612" />
              <p className="text-xs text-blue-700" data-id="element-1613">
                You will be redirected to choose your preferred payment method
                securely.
              </p>
            </div>

            <div className="flex space-x-3" data-id="element-1614">
              <button onClick={onClose} disabled={isProcessing} className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors" data-id="element-1615">
                Cancel
              </button>
              <button onClick={handleProceed} disabled={isProcessing} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-70 flex items-center justify-center transition-colors shadow-lg shadow-blue-200" data-id="element-1616">
                {isProcessing ? <Loader2 className="h-5 w-5 animate-spin" data-id="element-1617" /> : <>
                    Proceed
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-1618" />
                  </>}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}